<template>
  <div class="wrapper">
    <div ref="pagesRef" class="load-more">
      <div
        v-for="(page, index) in state.pageData"
        :id="computedPageId(page)"
        :key="`load-more_${page.id}`"
        ref="childPagesRef"
        class="load-more-outer"
        :class="{
          'hidden-article': page.id !== currentPage?.id,
        }"
      >
        <component :is="index === 0 ? 'div' : TransitionHeight">
          <component :is="index === 0 ? 'div' : Suspense">
            <template #default>
              <keep-alive>
                <div class="page" :class="{ loading: state.loading && isLast(index) }">
                  <DynamicArticle v-if="page" :data="page" :_index="index" />
                  <SterBanner v-if="index === 0" />
                </div>
              </keep-alive>
            </template>
          </component>
        </component>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useThrottleFn } from '@vueuse/core';
import { useRouter } from 'vue-router';
import useGetUrl from '~/composables/useArticleUrl';
import useRoutesStore from '~/stores/routes';
import articlesConfig, { type IArticlesConfig } from '~~/configs/articles.config';
import useHistory from '~/composables/useHistory';
import DynamicArticle from '~/components/page/DynamicArticle.vue';
import { TransitionHeight } from '#components';
import type { IArticles, TArticle, TArticleType } from '~~/types/local_types';
import { Suspense } from 'vue';
import usePageStore from '~/stores/page';
import { storeToRefs } from 'pinia';

const cms = useCms();
const { objectKeysToCamelCase } = formatUtils();
const { setRouteData } = useRoutesStore();
const router = useRouter();

const pageStore = usePageStore();
const { setCurrentPage } = pageStore;
const { currentPage } = storeToRefs(pageStore);

const maxArticles = 5;
const rerunTimeoutRef = ref<number | undefined>(undefined);
const updateTimeout = ref<number | undefined>(undefined);

const pagesRef = ref<HTMLElement | null>(null);
const childPagesRef = ref<HTMLElement[]>([]);

const listeners = new Map<HTMLElement, { click: EventListener; keydown: EventListener }>();

interface IProps {
  data: TArticle;
}
const props = withDefaults(defineProps<IProps>(), {});

interface IState {
  lastId: number | null;
  pageData: TArticle[];
  activePageId?: number | null;
  activePageData?: TArticle;
  loading: boolean;
  idle: boolean;
  articlesLoaded: number;
  initialized: boolean;
  endReached: boolean;
  sortedPool: TArticle[] | null;
}
const state = reactive<IState>({
  lastId: props.data.id,
  pageData: [props.data],
  loading: false,
  idle: true,
  activePageId: props.data.id,
  activePageData: props.data,
  articlesLoaded: 1,
  initialized: false,
  endReached: false,
  sortedPool: null,
});

// Rondt een datum naar beneden af op het dichtstbijzijnde veelvoud van `minutes` minuten
const roundToNearest = (date: Date, minutes: number) => {
  const ms = minutes * 60 * 1000;
  return new Date(Math.floor(date.getTime() / ms) * ms);
};

// Computed property to generate page IDs
const computedPageId = (page: TArticle) => {
  return `id${useGetUrl(page.articleType, page.slug).replaceAll('/', '-')}`;
};

// Helper to check if page is the last element
const isLast = (index: number) => index === state.pageData.length - 1;

const updateHeadAndUrl = (page: TArticle) => {
  const pageUrl = useGetUrl(page.articleType as TArticleType, page.slug);
  setRouteData(router.currentRoute.value.path, page.id as number, pageUrl);
  useHistory(page);

  usePageSeo({ content: page });
  if (import.meta.client) {
    const { splitType } = formatUtils();
    const { chapter1, chapter2 } = splitType(page?.articleType);
    sendAnalytics({ prefix: page?.articleType, chapter1, chapter2, slug: page?.slug });
  }
};

const loadMore = async () => {
  const articleTypeDefinition = articlesConfig.artikelen as IArticlesConfig;

  // 1. Check of we de pool al in het geheugen hebben
  if (state.sortedPool && state.sortedPool.length >= state.articlesLoaded) {
    const nextMatch = state.sortedPool[state.articlesLoaded - 1];

    if (nextMatch) {
      logMatch(nextMatch); // Debug logger
      state.pageData.push(nextMatch as TArticle);
      state.articlesLoaded++;
      state.loading = false;
      return; // Stop hier, we hebben geen API nodig
    }
  }

  // 2. Geen pool of einde pool bereikt? Haal (opnieuw) data op
  try {
    const zevenDagenGeleden = new Date();
    zevenDagenGeleden.setDate(zevenDagenGeleden.getDate() - 7);
    // Rond af op 5 minuten zodat de filterwaarde (en dus de request-URL) stabiel blijft
    // binnen hetzelfde tijdvak. Zonder afronding bevat elke call een unieke milliseconde-
    // timestamp, waardoor de response nooit gecached kan worden.
    const isoDatum = roundToNearest(zevenDagenGeleden, 5).toISOString();

    // ID's en Titels verzamelen van het hoofdartikel voor de match-logica
    const dossierMap = new Map(
      props.data.dossiers?.map((d) => [
        d.dossiersId?.id || d.id,
        d.dossiersId?.title || 'Onbekend dossier',
      ]),
    );
    const tagMap = new Map(
      props.data.tags?.map((t) => [t.tagsId?.id || t.id, t.tagsId?.name || 'Onbekende tag']),
    );
    const themeMap = new Map(
      props.data.themes?.map((th) => [
        th.themesId?.id || th.id,
        th.themesId?.title || 'Onbekend thema',
      ]),
    );

    // De bestaande ID arrays voor de API filter (gebruik de keys van de Maps)
    const dossierIds = Array.from(dossierMap.keys());
    const tagIds = Array.from(tagMap.keys());
    const themeIds = Array.from(themeMap.keys());

    // Query: Haal Fallback/Gerelateerde artikelen op
    const response = await cms.readItems('articles', {
        fields: articleTypeDefinition.fields,
        filter: {
          _and: [
            { status: { _eq: 'published' } },
            { id: { _neq: props.data.id } },
            {
              article_type: {
                _nin: ['paginas', 'externe-link', 'peilingtrends', 'opiniepanel/paginas', 'shorts'],
              },
            },
            {
              _or: [
                // Stap 1: Dossiers (< 7d)
                ...(dossierIds.length
                  ? [
                      {
                        _and: [
                          { dossiers: { dossiers_id: { _in: dossierIds } } },
                          { publish_time: { _gte: isoDatum } },
                        ],
                      },
                    ]
                  : []),
                // Stap 2: Tags (< 7d)
                ...(tagIds.length
                  ? [
                      {
                        _and: [
                          { tags: { tags_id: { _in: tagIds } } },
                          { publish_time: { _gte: isoDatum } },
                        ],
                      },
                    ]
                  : []),
                // Stap 3: Thema (< 7d)
                ...(themeIds.length
                  ? [
                      {
                        _and: [
                          { themes: { themes_id: { _in: themeIds } } },
                          { publish_time: { _gte: isoDatum } },
                        ],
                      },
                    ]
                  : []),
                // Stap 4: fallback (Gewoon de nieuwste, geen datum check meer)
                { status: { _eq: 'published' } },
              ],
            },
          ],
        },
        sort: ['-publish_time'],
        limit: 20,
    });

    const pool = objectKeysToCamelCase(response) as IArticles[];

    // 3. De Sortering: De hiërarchie toepassen met details
    state.sortedPool = pool
      .map((artikel) => {
        const isVers = new Date(artikel.publishTime) >= zevenDagenGeleden;

        // We verzamelen nu de titels van de matches
        const matchedDossierTitles =
          artikel.dossiers
            ?.filter((d) => dossierMap.has(d.dossiersId?.id || d.dossiersId))
            .map((d) => dossierMap.get(d.dossiersId?.id || d.dossiersId)) || [];

        const matchedTagTitles =
          artikel.tags
            ?.filter((t) => tagMap.has(t.tagsId?.id || t.tagsId))
            .map((t) => tagMap.get(t.tagsId?.id || t.tagsId)) || [];

        const matchedThemeTitles =
          artikel.themes
            ?.filter((th) => themeMap.has(th.themesId?.id || th.themesId))
            .map((th) => themeMap.get(th.themesId?.id || th.themesId)) || [];

        const hasDossier = matchedDossierTitles.length > 0 && isVers;
        const hasTag = matchedTagTitles.length > 0 && isVers;
        const hasTheme = matchedThemeTitles.length > 0 && isVers;
        let priority = 4;
        let reason = 'Fallback (Nieuwste)';
        let matchDetails = '';

        if (hasDossier) {
          priority = 1;
          reason = '🥇 Dossier Match';
          matchDetails = matchedDossierTitles.join(', ');
        } else if (hasTag) {
          priority = 2;
          reason = '🥈 Tag Match';
          matchDetails = matchedTagTitles.join(', ');
        } else if (hasTheme) {
          priority = 3;
          reason = '🥉 Thema Match';
          matchDetails = matchedThemeTitles.join(', ');
        }

        return {
          ...artikel,
          debugPriority: priority,
          debugReason: reason,
          debugMatchDetails: matchDetails,
          matchCount:
            matchedDossierTitles.length + matchedTagTitles.length + matchedThemeTitles.length,
        };
      })
      .sort((a, b) => {
        // 1. Prioriteit (1 t/m 4)
        if (a.debugPriority !== b.debugPriority) return a.debugPriority - b.debugPriority;

        // 2. Als prioriteit gelijk is, sorteer op aantal matches (meeste eerst)
        if (a.matchCount !== b.matchCount) return b.matchCount - a.matchCount;

        // 3. Fallback op datum
        return new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime();
      });

    // 4. Pak het eerste artikel uit de nieuwe pool
    const match = state.sortedPool[state.articlesLoaded - 1];

    if (match) {
      logMatch(match);
      state.pageData.push(match as TArticle);
      state.articlesLoaded++;
    } else {
      state.endReached = true;
    }
  } catch (error) {
    console.error('Waterfall load failed:', error);
    state.endReached = true;
  } finally {
    state.loading = false;
    state.idle = true;
  }
};

// Helper functie voor schone logs
const logMatch = (
  match: IArticles & { debugReason: string; debugPriority: number; debugMatchDetails?: string },
) => {
  const { isEnv } = useUrl();
  const dev = import.meta.dev || (import.meta.client && isEnv('dev'));
  if (!dev) return;

  console.group(`📄 Artikel Gekozen (Positie: ${state.articlesLoaded})`);
  console.log(`Titel: ${match.title}`);
  console.log(`Reden: %c${match.debugReason}`, 'color: #007bff; font-weight: bold');

  if (match.debugMatchDetails) {
    console.log(`Matches: %c${match.debugMatchDetails}`, 'color: #28a745; font-style: italic');
  }

  console.log(`Prioriteit: ${match.debugPriority}`);
  console.groupEnd();
};

let observer: IntersectionObserver | null = null;
const observeLastElement = () => {
  if (observer) observer.disconnect();

  if (state.endReached) return;

  const lastEl = childPagesRef.value[childPagesRef.value.length - 1];
  if (lastEl) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !state.loading && state.idle) {
          state.loading = true;
          state.idle = false;
          loadMore();
        }
      });
    });
    observer.observe(lastEl);
  }
};

// we switch active page based on the article footer being visible
const isArticleFooterNoLongerVisible = (articleEl: HTMLElement): boolean => {
  const footerEl = articleEl.querySelector('.article-footer');

  if (!footerEl) return true;

  const rect = footerEl.getBoundingClientRect();
  return rect.bottom < 0;
};

const checkActivePage = () => {
  if (!childPagesRef.value.length) return;

  const pagesVisiblility = childPagesRef.value.map((page) => isArticleFooterNoLongerVisible(page));
  const firstVisibleIndex = pagesVisiblility.indexOf(false);

  const activePageData: TArticle | undefined = state.pageData[firstVisibleIndex];

  if (activePageData && state.activePageId !== activePageData.id) {
    state.activePageData = activePageData;
    setCurrentPage(activePageData);
    state.activePageId = activePageData.id;
  }
};

// Define throttled functions once
const throttledCheckActivePage = useThrottleFn(checkActivePage, 250, true, true);

watch(
  () => state.activePageId,
  (newValue) => {
    if (newValue) {
      updateHeadAndUrl(state.activePageData as TArticle);
      if (newValue === 1) state.endReached = true;
    }
  },
);

const initialize = () => {
  if (state.initialized) return;
  state.initialized = true;
  window.addEventListener('scroll', throttledCheckActivePage);
  // Call once to set initial active page
  checkActivePage();
};

const handleKeydown = (e: KeyboardEvent, href: string) => {
  if (e.key === 'Enter') {
    scrollToElement(e, href);
  }
};

const scrollToElement = (e: Event, href: string) => {
  if (href && href.includes('#')) {
    e.preventDefault();
    e.stopPropagation();
    const pageIdentifier = href.split('#')[1];
    if (pageIdentifier) {
      const target = document.getElementById(pageIdentifier);
      if (target) target.scrollIntoView();
    }
  }
};

watch(
  () => state.articlesLoaded,
  (newVal) => {
    if (newVal === maxArticles) state.endReached = true;
    // Re-observe the new last element
    nextTick(() => observeLastElement());
  },
);

let resizeObserver: ResizeObserver | undefined;

const cleanup = () => {
  window.removeEventListener('scroll', throttledCheckActivePage);
  if (observer) observer.disconnect();
  if (rerunTimeoutRef.value) {
    window.clearTimeout(rerunTimeoutRef.value);
    rerunTimeoutRef.value = undefined;
  }
};

/**
 * Scans all child pages for anchor links and sets up event listeners for click and keydown events.
 */
const scanAnchors = () => {
  childPagesRef.value.forEach((page) => {
    const anchorLinks = Array.from(page.querySelectorAll('a[href^="#"]')) as HTMLAnchorElement[];
    for (const el of anchorLinks) {
      const href = el.getAttribute('href');
      if (href && href.length > 1 && !listeners.has(el)) {
        const clickHandler = (e: Event) => scrollToElement(e, href);
        const keydownHandler = (e: Event) => handleKeydown(e as KeyboardEvent, href);

        el.addEventListener('click', clickHandler);
        el.addEventListener('keydown', keydownHandler);

        listeners.set(el, { click: clickHandler, keydown: keydownHandler });
      }
    }
  });
};

onMounted(() => {
  nextTick(() => {
    scanAnchors(); // Initial check for anchor links
    // Set up ResizeObserver to adjust links dynamically
    resizeObserver = new ResizeObserver(() => {
      if (updateTimeout.value) {
        clearTimeout(updateTimeout.value);
      }
      updateTimeout.value = window.setTimeout(() => {
        const event = new CustomEvent('load-more:update');
        document.dispatchEvent(event);
      }, 1000 / 10);
    });
    if (pagesRef.value && resizeObserver) {
      resizeObserver.observe(pagesRef.value);
    }
    // Re-run checks on changes to child elements
    watch(
      () => childPagesRef.value.length,
      () => {
        if (rerunTimeoutRef.value) clearTimeout(rerunTimeoutRef.value);
        rerunTimeoutRef.value = window.setTimeout(() => {
          state.idle = true;
          checkActivePage();
          observeLastElement();
          scanAnchors();
        }, 750);
      },
      { immediate: true },
    );
  });

  // Set initial history
  useHistory(state.pageData[0]);
  // Delay initialization for page transition load
  setTimeout(() => {
    // Optionally, if a global scroll flag is used
    // isScolling is defined in utils > extendedScroll.ts
    watch(
      window.isScrolling,
      (newValue) => {
        if (!newValue) {
          initialize();
        }
      },
      { immediate: true },
    );
  }, 1000);
});

onBeforeUnmount(() => {
  cleanup();
  // Clean up dynamic links
  listeners.forEach((handlers, el) => {
    el.removeEventListener('click', handlers.click);
    el.removeEventListener('keydown', handlers.keydown);
  });
  listeners.clear();
  if (resizeObserver && pagesRef.value) {
    resizeObserver.unobserve(pagesRef.value);
    resizeObserver.disconnect();
  }

  if (updateTimeout.value) {
    window.clearTimeout(updateTimeout.value);
  }
});
</script>

<style scoped lang="postcss">
.wrapper {
  min-height: 100vh;
}

.load-more {
  overflow: hidden;
}

.load-more-outer {
  &:last-child {
    > .page {
      margin-bottom: 0;
      &:after {
        content: unset;
      }
    }
  }
}

.page {
  position: relative;
  will-change: margin-bottom;
  transition: margin-bottom 0.25s ease;
  margin-bottom: var(--unit-60);
  @media (--md-viewport) {
    margin-bottom: var(--unit-52);
  }
  @media (--lg-viewport) {
    margin-bottom: var(--unit-52);
  }
  &:after {
    will-change: background;
    position: absolute;
    content: '';
    width: 100%;
    height: var(--unit-16);
    transition:
      bottom 0.25s ease,
      background 0.25s linear;

    bottom: calc(var(--unit-36) * -1);

    @media (--md-viewport) {
      bottom: calc(var(--unit-36) * -1);
    }

    @media (--lg-viewport) {
      bottom: calc(var(--unit-40) * -1);
    }

    background: rgba(241, 242, 249, 0.4);
    box-shadow:
      0 -1px 0 0 rgba(227, 229, 242, 0.2) inset,
      0 2px 6px 0 rgba(229, 231, 246, 0.2) inset;
  }
  &.loading:after {
    background: rgba(241, 242, 249, 0.4)
      linear-gradient(90deg, transparent 35%, hsl(var(--color-primary-50)) 35%);
    background-size: 120px 100%;
    animation: moveBackground 10s linear infinite;
  }
  @keyframes moveBackground {
    0% {
      background-position: -100% 0;
    }
    100% {
      background-position: 100% 0;
    }
  }
}
.loader {
  padding: var(--unit-60);
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
}
</style>
